'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { Icon } from '@benzinga/core-ui';
import { faThumbsUp } from '@fortawesome/pro-light-svg-icons/faThumbsUp';
import { faThumbsDown } from '@fortawesome/pro-light-svg-icons/faThumbsDown';
import { useUser } from '@benzinga/user-context';
import { SessionContext } from '@benzinga/session-context';
import { AltOfferingsManager } from '@benzinga/alts-manager';
import { ErrorBoundary } from '@benzinga/core-ui';

const CommentsEmbedScript = React.lazy(() =>
  import('@benzinga/comments-ui').then(module => ({ default: module.CommentsEmbedScript })),
);
const CommentsDrawerButton = React.lazy(() =>
  import('@benzinga/comments-ui').then(module => ({ default: module.CommentsDrawerButton })),
);
const CommentsDrawer = React.lazy(() =>
  import('@benzinga/comments-ui').then(module => ({ default: module.CommentsDrawer })),
);

export interface AltOfferingFormProps {
  id: number;
  offer_name: string;
  slug?: string;
  show_comment_btn?: boolean;
}

export const AltOfferingForm: React.FC<AltOfferingFormProps> = ({ id, offer_name, show_comment_btn = false, slug }) => {
  const [isCommentsDrawerOpen, setIsCommentsDrawerOpen] = React.useState(false);
  const [email, setEmail] = React.useState<string>('');
  const [name, setName] = React.useState<string>('');
  const [why, setWhy] = React.useState<string>('');
  const [didSubmit, setDidSubmit] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>('');

  const [status, setStatus] = React.useState<string>();

  const user = useUser();

  const session = React.useContext(SessionContext);

  React.useEffect(() => {
    if (user) {
      setName(user.displayName);
      setEmail(user.email);
    }
  }, [user]);

  const handleSubmit = React.useCallback(() => {
    let isValid = true;

    const emailRegex = /([a-zA-Z0-9._!#$%+^&*()[\]<>-]+)@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+/gi;
    if (!emailRegex.test(email)) {
      isValid = false;
      setError('Invalid email.');
    }

    if (isValid) {
      session
        .getManager(AltOfferingsManager)
        .submitOfferFeedback(id, {
          email: email,
          name: name,
          status: status ?? '',
          text: why ?? '',
        })
        .then(res => {
          if (res.ok) {
            const { message, status } = res.ok;
            if (status === 'success') {
              setError('');
              setDidSubmit(true);
            } else {
              setError(message);
              console.log('Something went wrong.');
            }
          } else {
            setError('Something went wrong!!');
            console.log('Something went wrong!!');
          }
        })
        .catch(err => {
          console.log('Something went wrong!');
          console.log(err);
        });
    }
  }, [email, id, name, session, status, why]);

  const handleCloseCommentsSidebar = () => {
    if (!show_comment_btn) return;
    setIsCommentsDrawerOpen(false);
  };

  const handleArticleCommentOpenAnalyticsEvent = () => {
    import('@benzinga/comments-ui').then(module => {
      module.handleCommentsAnalyticsEvent('Offering', 'open-drawer', session);
    });
  };

  const handleOpenCommentsSidebar = () => {
    if (!show_comment_btn) return;
    setIsCommentsDrawerOpen(true);
    handleArticleCommentOpenAnalyticsEvent();
  };

  return (
    <AltOfferingFormContainer>
      <h2>Do you think {offer_name} is a good investment?</h2>

      {didSubmit && <SubmitMessage>Thank you for submitting your opinion!</SubmitMessage>}
      {!didSubmit && (
        <ButtonGroupContainer>
          <ButtonContainer
            className={status === 'YES' ? 'thumb-up active' : 'thumb-up'}
            onClick={() => setStatus('YES')}
          >
            <Icon className={'icon text-gray'} icon={faThumbsUp} /> YES
          </ButtonContainer>
          <ButtonContainer
            className={status === 'NO' ? 'thumb-down active' : 'thumb-down'}
            onClick={() => setStatus('NO')}
          >
            <Icon className={'icon text-gray'} icon={faThumbsDown} /> NO
          </ButtonContainer>
          {show_comment_btn && (
            <ErrorBoundary name="offering-comments-section">
              <CommentsEmbedScript
                isInjected={isCommentsDrawerOpen}
                type="Offering"
                url={`https://www.benzinga.com/alternative-investments/listing/${slug}`}
              />
            </ErrorBoundary>
          )}

          {show_comment_btn && (
            <ErrorBoundary name="offering-comments-button">
              <CommentsDrawerButton commentMessage={'Share Your Thoughts!'} onClick={handleOpenCommentsSidebar} />
            </ErrorBoundary>
          )}

          <ErrorBoundary name="offering-comments-drawer">
            <CommentsDrawer isOpen={isCommentsDrawerOpen} onClose={handleCloseCommentsSidebar} />
          </ErrorBoundary>
        </ButtonGroupContainer>
      )}
      {status && !didSubmit && (
        <>
          <h3>Why?</h3>
          <textarea onChange={e => setWhy(e.target.value)} value={why} />
          <h3>
            Name<sup>*</sup>
          </h3>
          <input onChange={e => setName(e.target.value)} value={name} />
          <h3>
            Email<sup>*</sup>
          </h3>
          <input onChange={e => setEmail(e.target.value)} value={email} />
          <br />
          <br />
          <button onClick={handleSubmit} type="submit">
            Submit
          </button>
        </>
      )}
      {error && <SubmitMessage className="error mt-2">{error}</SubmitMessage>}
    </AltOfferingFormContainer>
  );
};

const AltOfferingFormContainer = styled.div`
  .success-text {
    font-weight: bold;
    font-style: italic;
  }
  h2 {
    font-size: 1.6rem;
    margin-bottom: 0.8rem;
  }
  h3 {
    font-size: 1rem;
    margin-top: 1rem;
    margin-bottom: 0.2rem;
  }
  textarea,
  input {
    border: solid 1px #ddd;
    border-radius: 0.25rem;
    width: 200px;
    padding: 0.25rem 0.5rem;
  }
  textarea {
    width: 100%;
  }
  button {
    background-color: #7ba5ff;
    border: solid 1px #0051ff;
    border-radius: 0.25rem;
    color: white;
    font-weight: bold;
    padding: 0.5rem 1rem;
  }
  .comments-count-button {
    height: 100%;
    button {
      height: 100%;
      background: transparent;
      color: black;
      border: 1px solid #ddd;
      border-radius: 0.5rem;
      color: rgb(45, 45, 45);
      font-weight: 500;
      font-size: 16px;
      padding: 12px;
      &:hover {
        color: black;
        background-color: transparent;
      }
    }
  }
`;

const SubmitMessage = styled.div`
  padding: 0.5rem;
  border: solid 2px #08865b;
  border-radius: 8px;
  background-color: #d8ffd8;
  font-weight: bold;
  &.error {
    background-color: red;
  }
`;

const ButtonGroupContainer = styled.div`
  /* text-align: center; */
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const ButtonContainer = styled.div`
  display: inline-flex;
  padding: 0.5rem 1rem;
  border: solid 1px #ddd;
  border-radius: 0.5rem;
  font-weight: bold;
  cursor: pointer;
  .icon {
    margin-top: 0.25rem;
    margin-right: 0.5rem;
  }
  &.active {
    background-color: #fafafa;
  }
  &.active {
    &.thumb-up {
      color: darkgreen;
      border-color: darkgreen;
    }
    &.thumb-down {
      color: darkred;
      border-color: darkred;
    }
  }
`;
